import {Component, inject} from '@angular/core';
import {AsyncPipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {filter, map, Observable, startWith} from "rxjs";
import {GameService} from "../../services/game.service";
import {NavScopeService} from "../../services/nav-scope.service";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";

interface IPageHeaderVm {
  show: boolean;
  domain?: string;
  title?: string;
}

/**
 * Shell-level page header (Release 1.19 IA pass, Stage 4).
 *
 * Renders a "Domain / Page" breadcrumb plus a scope chip beneath the toolbar on every
 * feature screen, so you always know where you are and whose (which game's) data you're
 * looking at. Driven by the router + nav model + active game — no per-page wiring.
 * Hidden on chrome routes (landing, auth, error pages).
 */
@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [AsyncPipe, MatIcon],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss',
})
export class PageHeaderComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private gameService = inject(GameService);
  private navScope = inject(NavScopeService);

  public activeGame$: Observable<IGameQueryResult | undefined> = this.gameService.selectActiveGame$();

  public vm$: Observable<IPageHeaderVm> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.buildViewModel()),
  );

  private buildViewModel(): IPageHeaderVm {
    const url = this.router.url;
    if (this.isChromeRoute(url)) {
      return {show: false};
    }
    const resolved = this.navScope.resolvePage(url);
    const title = resolved.title ?? this.deepestRouteTitle();
    if (!title) {
      return {show: false};
    }
    // Suppress the domain crumb when it just repeats the page label (single-page
    // domains, e.g. Charities → avoid "Charities › Charities").
    const domain = resolved.domain && resolved.domain !== title ? resolved.domain : undefined;
    return {show: true, domain, title};
  }

  /** Title of the most deeply-activated route (fallback for pages not in the nav model). */
  private deepestRouteTitle(): string | undefined {
    let route = this.route.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.title;
  }

  /** Routes that shouldn't get a page header (landing, auth flow, error pages). */
  private isChromeRoute(url: string): boolean {
    const path = url.split('?')[0].replace(/^\/+/, '');
    if (path === '') {
      return true;
    }
    return /^(auth|not-authorized|not-admin|logout|forgot-my-password)(\/|$)/.test(path);
  }
}
